import * as Haptics from 'expo-haptics';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IndexScrollbar } from '@/components/index-scrollbar';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
} from 'react-native';
import { useDebouncedCallback } from 'use-debounce';
import { useKeyboardVisibility } from '@/hooks/use-keyboard-visibility';

export const VISIBLE_SCROLL_DISTANCE_THRESHOLD = 100;
export const VISIBLE_TIMEOUT_MS = 500;

interface IndexScrollbarContextType {
  onScrollViewScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => void;
}

export const IndexScrollbarContext =
  createContext<IndexScrollbarContextType | null>(null);

export const IndexScrollbarActiveContext = createContext<boolean>(false);

export interface Props {
  children?: React.ReactNode;
  enabled?: boolean;
  indexes: string[];
  indexMap: { [index: string]: number };
  onScrollToIndex: (index: number) => void;
}

const ProviderComponent: React.FC<Props> = ({
  children,
  enabled = true,
  indexes,
  indexMap,
  onScrollToIndex,
}) => {
  const isKeyboardVisible = useKeyboardVisibility();

  const lastScrollPositionRef = useRef(0);
  const scrollPositionRef = useRef(0);
  const isTouchingRef = useRef(false);

  const [isActive, setIsActive] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Hides the scrollbar after the given timeout
  const hideScrollbar = useDebouncedCallback(() => {
    lastScrollPositionRef.current = scrollPositionRef.current;
    setIsVisible(false);
  }, VISIBLE_TIMEOUT_MS);

  useEffect(() => {
    if (isKeyboardVisible) {
      // Hide the scrollbar whenever keyboard becomes visible
      hideScrollbar.flush();
      setIsVisible(false);
    }
  }, [hideScrollbar, isKeyboardVisible]);

  const onScrollViewScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollPositionRef.current = e.nativeEvent.contentOffset.y;

      if (
        Math.abs(scrollPositionRef.current - lastScrollPositionRef.current) >=
        VISIBLE_SCROLL_DISTANCE_THRESHOLD
      ) {
        setIsVisible(true);
      }

      // Hide the scrollbar only if the user is not interacting with it
      if (!isTouchingRef.current) {
        hideScrollbar();
      }
    },
    [hideScrollbar],
  );

  const contextValue = useMemo<IndexScrollbarContextType>(
    () => ({ onScrollViewScroll }),
    [onScrollViewScroll],
  );

  return (
    // ScrollViews can lose their scroll position when re-rendering so separate
    // contexts ensure components only listen to what they have to
    <IndexScrollbarContext.Provider value={contextValue}>
      <IndexScrollbarActiveContext.Provider value={isActive}>
        {children}
        {enabled ? (
          <IndexScrollbar
            indexes={indexes}
            isVisible={isVisible}
            onSelect={(index) => {
              if (typeof indexMap[index] === 'number') {
                // Provide haptic feedback when an index is selected
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                onScrollToIndex(indexMap[index]);
                setIsActive(true);
              }
            }}
            onTouchEnd={() => {
              isTouchingRef.current = false;
              hideScrollbar();
              setIsActive(false);
            }}
            onTouchStart={() => {
              isTouchingRef.current = true;

              // Cancel any pending callbacks to hide the scrollbar if the user
              // starts interacting with the scrollbar
              hideScrollbar.cancel();
            }}
          />
        ) : null}
      </IndexScrollbarActiveContext.Provider>
    </IndexScrollbarContext.Provider>
  );
};

export const IndexScrollbarContextProvider: React.FC<Props> = (props) => {
  return Platform.OS === 'web' ? (
    <>{props.children}</>
  ) : (
    <ProviderComponent {...props} />
  );
};

export const useIndexScrollbarContext = () => {
  return useContext(IndexScrollbarContext);
};

export const useIndexScrollbarActiveContext = () => {
  return useContext(IndexScrollbarActiveContext);
};
